import { useLocalStorage } from '@components/hooks';
import { useMemo, useState } from 'react';

const useProductCard = product => {
  const {
    content: {
      akv_aktions_info_website: akvAktionsInfoWebsite,
      aktion_inline: aktionInline,
      aktion_typ: aktionTyp,
      ist_rabattiert: istRabattiert,
      tarif_farbe_rabattiert: tarifFarbeRabattiert,
      tarif_farbe: tarifFarbe,
      selected_action_code: selectedActionCode,
      prices: cardPrices,
      details,
      detailText,
    },
    price_info_modal: priceInfoModal,
    legal_text_calc: legalTextCalc,
    grundpreis_basis: grundpreisBasis,
    grundpreis_label: grundpreisLabel,
    grundpreis_mit_rabatt: grundpreisMitRabatt,
    grundpreis_subtext: grundpreisSubtext,
    grundpreis_unit: grundpreisUnit,
    grundpreis,
    energiepreis_without_discount: energiepreisWithoutDiscount,
    has_valid_discount: hasValidDiscount,
    legal_text_card_rabattiert: legalTextCardRabattiert,
    legal_text_card: legalTextCard,
    verbrauchspreis_basis: verbrauchspreisBasis,
    verbrauchspreis_label: verbrauchspreisLabel,
    verbrauchspreis_mit_rabatt: verbrauchspreisMitRabatt,
    verbrauchspreis_subtext: verbrauchspreisSubtext,
    verbrauchspreis_spot_max: verbrauchspreisSpotProductMax,
    verbrauchspreis_spot_min: verbrauchspreisSpotProductMin,
    verbrauchspreis_unit: verbrauchspreisUnit,
    verbrauchspreis,
    type,
    title,
    calculated,
    area,
    is_spot_product: isSpotProduct,
  } = product;

  // Determine types, discount availability and status
  const isRabattAktion = aktionTyp === 'rabatt';
  const isAkvAktion = aktionTyp === 'akv';
  const isBusiness = type.startsWith('business_');
  const isDiscountAvailable = hasValidDiscount;
  const isDiscountActive = istRabattiert;

  // Determine color & legal text for the product card
  const productColor = isDiscountActive ? tarifFarbeRabattiert : tarifFarbe;
  const legalText = isDiscountActive ? legalTextCardRabattiert : legalTextCard;

  // Determine Verbrauchspreis and Grundpreisproperties
  const verbrauchspreise = {
    verbrauchspreis,
    verbrauchspreisBasis,
    verbrauchspreisLabel: `${verbrauchspreisLabel}${isDiscountActive && verbrauchspreisSubtext ? ' inkl. Rabatt:' : ':'}`,
    verbrauchspreisMitRabatt,
    verbrauchspreisUnit,
    verbrauchspreisSubtext,
    isVerbrauchspreisRabattiert:
      isDiscountActive && verbrauchspreisSubtext?.length > 0,
  };

  const grundpreise = {
    grundpreis,
    grundpreisBasis,
    grundpreisLabel: `${grundpreisLabel}${isDiscountActive && grundpreisSubtext ? ' inkl. Rabatt:' : ':'}`,
    grundpreisMitRabatt,
    grundpreisUnit,
    grundpreisSubtext,
    isGrundPreisRabattiert: isDiscountActive && grundpreisSubtext?.length > 0,
  };

  // Set Aktionsinfo content
  const getDefaultAktion = () => {
    if (aktionTyp === 'akv') {
      return (
        selectedActionCode ||
        aktionInline?.find(aktion => aktion.aktion_typ === 'akv')?.aktion_code
      );
    }
    if (aktionTyp === 'rabatt') {
      return selectedActionCode || aktionTyp;
    }
  };

  const [selectedAktion, setSelectedAktion] = useState(getDefaultAktion());

  const getAktionsInfo = () => {
    const aktionsBedingung = akvAktionsInfoWebsite;
    const rabattBedingung = aktionInline?.find(
      aktion => aktion.aktion_typ === selectedAktion,
    )?.rabatt_aktion_info_website;

    return isDiscountAvailable && !isDiscountActive
      ? isAkvAktion
        ? aktionsBedingung
        : rabattBedingung
      : null;
  };
  const aktionsInfo = getAktionsInfo();

  const aktionInfoBoxes = useMemo(() => {
    return aktionInline?.find(aktion =>
      isAkvAktion
        ? aktion.aktion_code === selectedAktion
        : aktion.aktion_typ === selectedAktion,
    )?.aktion_info;
  }, [selectedAktion, aktionInline]);

  const [storage] = useLocalStorage('generalProductRecommender');
  const isSelected = storage?.[type]?.title === title;

  // Set other price info content
  const { main_price, unit, subline } = cardPrices[0];
  const cardPrice = [
    {
      price: main_price,
      unit,
      subline: isSpotProduct ? 'Verbrauchspreis' : subline,
    },
  ];

  const getPriceSubline = () => {
    if (isSpotProduct) {
      return {
        lightContent: '',
        boldContent: `heute: ${verbrauchspreisSpotProductMin}-${verbrauchspreisSpotProductMax} Cent/kWh`,
      };
    }

    if (!isDiscountActive) {
      return {
        lightContent: '',
        boldContent: unit,
      };
    }

    if (calculated) {
      return {
        lightContent: `statt ${energiepreisWithoutDiscount} ${unit}`,
        boldContent: '',
      };
    }

    return {
      lightContent: verbrauchspreise.verbrauchspreisSubtext || unit,
      boldContent: '',
    };
  };
  const priceSubline = getPriceSubline();

  const getPriceModalConfig = () => ({
    header:
      isSpotProduct && priceInfoModal?.title ? priceInfoModal.title : title,
    detailText:
      isSpotProduct && priceInfoModal?.text ? priceInfoModal.text : detailText,
    showButton: Boolean(isSpotProduct && priceInfoModal?.text),
    showTable: Boolean(!isSpotProduct && details?.length && !details[0].id),
    showLegalText: Boolean(!isSpotProduct && legalTextCalc[area]),
  });
  const priceModal = getPriceModalConfig();

  return {
    aktionInfoBoxes,
    aktionsInfo,
    grundpreise,
    isAkvAktion,
    isDiscountActive,
    isDiscountAvailable,
    isRabattAktion,
    isSelected,
    legalText,
    productColor,
    selectedAktion,
    setSelectedAktion,
    verbrauchspreise,
    isSpotProduct,
    cardPrice,
    priceSubline,
    priceModal,
  };
};

export default useProductCard;
